import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { api, errMsg, fmtTime, fmtDate, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/Badges";
import DocList from "@/components/DocList";
import PageHeader from "@/components/PageHeader";
import ChangePassword from "@/components/ChangePassword";
import { Stars, StarRow } from "@/components/Rating";
import { t } from "@/lib/i18n";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [docs, setDocs] = useState([]);
  const [ratings, setRatings] = useState([]);
  useEffect(() => { refresh(); if (user.role !== "admin") api.get("/me/ratings").then((r) => setRatings(r.data)); }, [user.role, refresh]);
  const [docType, setDocType] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/me/documents").then((r) => setDocs(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType || "Supporting Document");
    setBusy(true);
    try { await api.post("/me/documents", fd); toast.success(t("Document uploaded")); setDocType(""); load(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const resubmit = async () => {
    try { await api.post("/me/resubmit"); toast.success(t("Resubmitted for review")); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };

  const rows = [["Contact", user.name], ["Company", user.company_name], ["Email", user.email], ["Phone", user.phone], ["Address", user.address], ["Reg. number", user.registration_number], ["VAT", user.vat_number], ["Country", user.country], ["Currency", user.currency]];
  if (user.git_insurance) rows.push(["GIT insurer", user.git_insurance.insurer], ["GIT policy", user.git_insurance.policy_number], ["GIT cover", money(user.git_insurance.cover_amount, user.git_insurance.currency)], ["GIT expiry", fmtDate(user.git_insurance.expiry_date)]);

  return (
    <div className="max-w-4xl">
      <PageHeader kicker={`${t(user.role)} · ${user.public_code}`} title={t("Profile")} />
      <div className="grid md:grid-cols-2 gap-6">
        <div className="hf-card p-6">
          <div className="flex justify-between items-center mb-4"><p className="font-display font-bold">{t("Company details")}</p>{user.role !== "admin" && <StatusBadge status={user.status} testId="profile-status" />}</div>
          <dl className="space-y-3 text-sm">
            {rows.map(([k, v]) => <div key={k} className="flex justify-between gap-4"><dt className="text-slate-500">{t(k)}</dt><dd className="font-semibold text-right break-words min-w-0">{v || "—"}</dd></div>)}
          </dl>
          {user.status === "declined" && <button onClick={resubmit} className="hf-btn-primary w-full mt-6" data-testid="btn-resubmit">{t("Resubmit for review")}</button>}
        </div>
        {user.role !== "admin" && (
          <div className="hf-card p-6 space-y-4">
            <p className="font-display font-bold">{t("Registration papers")}</p>
            <DocList docs={docs} testPrefix="profile-doc" />
            <div className="pt-4 border-t space-y-3">
              <input value={docType} onChange={(e) => setDocType(e.target.value)} placeholder={t("Document type (e.g. Insurance 2026)")} className="hf-input" data-testid="profile-doc-type-input" />
              <label className={`hf-btn-ghost w-full cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`}>
                <Upload className="w-4 h-4" /> {busy ? t("Uploading…") : t("Upload document")}
                <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" data-testid="profile-doc-upload-input" onChange={(e) => upload(e.target.files[0])} />
              </label>
            </div>
          </div>
        )}
      </div>
      <ChangePassword />
      {user.role !== "admin" && (
        <div className="hf-card p-6 mt-6" data-testid="profile-ratings">
          <div className="flex justify-between items-center mb-4"><p className="font-display font-bold">{t("Your reputation")}</p><Stars rating={{ avg: user.rating_avg || 0, count: user.rating_count || 0 }} testId="profile-rating-summary" /></div>
          {ratings.length === 0 && <p className="text-sm text-slate-500">{t("No ratings yet. Complete loads to build your reputation.")}</p>}
          <ul className="divide-y">
            {ratings.map((r) => (
              <li key={r.id} className="py-3">
                <div className="flex justify-between gap-3"><StarRow value={r.stars} size="w-4 h-4" /><span className="font-mono text-[10px] text-slate-400">{r.rater_code} · {fmtTime(r.created_at)}</span></div>
                <p className="text-xs text-slate-500 mt-1">{r.load_summary}</p>
                {r.comment && <p className="text-sm mt-1 break-words">"{r.comment}"</p>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
